import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellNotifications16 } from 'shared/assets/icons';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { reminderTimes } from 'shared/constants';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { MetaInteraction } from '../meta-interaction/meta-interaction';

export type MetaReminderProps = {
  timeFormat: TimeFormatOptions;
  value?: number | null;
  onChange?: (time?: number) => void;
};

export const MetaReminder: React.FC<MetaReminderProps> = ({
  timeFormat,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();

  const parsedValue = useMemo(
    () => (value ? parseDateTime(value, timeFormat) : undefined),
    [timeFormat, value],
  );

  const onShortcut = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if Shift is pressed
      if (e.shiftKey) {
        return;
      }

      if (!menuOpen) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openMenu();
      }
    },
    [menuOpen, openMenu],
  );

  useKeysDown(['KeyR'], onShortcut, {
    ignoreInputFields: true,
    capture: true,
  });

  const _onChange = (time?: number) => {
    onChange?.(time);
    closeMenu();
  };

  const onClear = () => {
    onChange?.();
    closeMenu();
  };

  return (
    <>
      <MetaInteraction
        ref={buttonRef}
        icon={Bell}
        onClick={openMenu}
        placeholder={t('dialogs.task-detail.meta.reminder.placeholder')}
        label={parsedValue}
        onClear={parsedValue ? onClear : undefined}
        selected={menuOpen}
      />
      {menuOpen && (
        <TimePickerMenu
          referenceElement={buttonRef}
          timeFormat={timeFormat}
          value={value}
          onChange={_onChange}
          onCancel={closeMenu}
          optionIcon={BellNotifications16}
          hasCustomInput
          canClear
          options={reminderTimes}
        />
      )}
    </>
  );
};
